import classes from 'classnames';
import { EmployerInfoData } from 'hooks/webapi/foxentry/FoxentryApiTypes';
import { useCallback, useState } from 'react';

import { Typography, Link, Autocomplete, Input } from 'components';
import ErrorMessage from 'components/ErrorMessage/ErrorMessage';
import { useTranslation } from 'react-i18next';
import { getEmployerName } from 'utils/employerClient';

import { useCompaniesByNameMutation } from 'hooks/webapi/foxentry/useCompaniesByNameMutation';
import {
  useCompaniesByIdMutation,
  EmployerOption,
} from 'hooks/webapi/foxentry/useCompaniesByIdMutation';
import styles from './EducationEmployment.module.scss';

type Props = {
  className?: string;
  onChange: (value: string | EmployerInfoData | undefined) => void;
  value: string | EmployerOption | undefined;
  error?: string;
  errorClassName?: string;
  disabled?: boolean;
  isTouched?: boolean;
  isOSVC?: boolean;
};

export default function EducationEmploymentInput({
  className,
  onChange,
  value,
  error,
  errorClassName,
  disabled = false,
  isTouched = false,
  isOSVC = false,
}: Props) {
  const { t } = useTranslation('education_employment');

  const [inputMode, setInputMode] = useState<'search' | 'manual'>('search');

  const { mutateAsync: getCompaniesByName } = useCompaniesByNameMutation();
  const { mutateAsync: getCompaniesById } = useCompaniesByIdMutation();

  // variable used to show different text for self-employed
  const selfemployedPostfix = isOSVC ? '_selfemployed' : '';

  const renderAutocompleteOption = useCallback((option: EmployerOption) => {
    return (
      <>
        <div className={styles['dropdown-company-info']}>
          <span>{option.data.name}</span>
          {option.data.registrationNumber && (
            <Typography
              color="muted"
              dataTest="EducationEmployment-identificationNumber"
            >
              {t('identification_number')}
              {option.data.registrationNumber}
            </Typography>
          )}
        </div>
        <div className={styles['dropdown-address']}>
          {option.data.address && (
            <Typography
              size="s"
              color="auxiliary"
              dataTest="EducationEmployment-street"
            >
              {option.data.address?.data.streetWithNumber}
              {', '}
              {option.data.address?.data.city}
            </Typography>
          )}
        </div>
      </>
    );
  }, []);

  const handleToggleInputMode = () => {
    if (inputMode === 'search') {
      setInputMode('manual');
    } else {
      setInputMode('search');
    }
    onChange('');
  };

  const fetchFunction = (query: string) => {
    const isNotANumber = Number.isNaN(Number(query));
    if (query) {
      if (isNotANumber) {
        return getCompaniesByName({ companyName: query });
      }
      return getCompaniesById({ registrationNumber: Number(query) });
    }
    return Promise.resolve({ response: { results: [] } });
  };

  return (
    <div className={className}>
      <div>
        {inputMode === 'manual' && (
          <Input
            value={value as string | undefined}
            onChange={(event) => onChange(event.target.value)}
            placeholder={t(`manual_input_placeholder${selfemployedPostfix}`)}
            className={styles.input}
            dataTest="education-employment-manual-input"
            disabled={disabled}
          />
        )}
        {inputMode === 'search' && (
          <Autocomplete
            className={styles.autocompleteContainer}
            renderOption={renderAutocompleteOption}
            fetchFunction={fetchFunction}
            withSearchIcon
            onOptionSelect={(option) => onChange(option)}
            getOptionLabel={(option: EmployerInfoData) => option.data.name}
            placeholder={t('search')}
            dropdownItemClassName={styles['dropdown-content']}
            dataTest="education-employment-autocomplete"
            defaultValue={getEmployerName(value)}
            disabled={disabled}
          />
        )}
        {error && isTouched && (
          <div className={classes(styles['error-message'], errorClassName)}>
            <ErrorMessage errors={error} />
          </div>
        )}
      </div>

      <Link
        onClick={handleToggleInputMode}
        to="/"
        // className for testing / analytics purposes, pls don't change
        className={styles['not-found-link']}
        dataTest="education-employment-toggle-manual"
      >
        {inputMode === 'manual'
          ? t(`search_for_employer${selfemployedPostfix}`)
          : t(`not_found_text${selfemployedPostfix}`)}
      </Link>
    </div>
  );
}
